import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, CartesianGrid } from 'recharts';

function SparkleChartLoading() {
  const [chartData, setChartData] = useState([]);
  const animationRef = useRef(null);
  const sparkleIdRef = useRef(0);
  const currentSparkleRef = useRef(null);

  const months = useMemo(() => ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'], []);

  // Initialize base chart data once
  useEffect(() => {
    setChartData(
      months.map((month) => ({
        month,
        sparkle: null,
      })),
    );
  }, [months]);

  // Generate a random sparkle path that looks like a real exchange rate curve
  const generateSparklePath = useCallback(() => {
    const points = [];

    // Random starting point
    let currentY = Math.random() * 4 + 4; // Start between 4 and 8 (middle range)

    // Decide how many wave cycles (1-3 ups and downs)
    const waveCycles = Math.floor(Math.random() * 3) + 1; // 1, 2, or 3 cycles
    const totalPoints = months.length;
    const pointsPerCycle = totalPoints / waveCycles;

    months.forEach((month, index) => {
      // Create smooth wave pattern with natural variations
      const cyclePosition = (index % pointsPerCycle) / pointsPerCycle; // 0 to 1 within each cycle
      const wave = Math.sin(cyclePosition * Math.PI * 2) * 2; // Sine wave for smooth ups and downs

      // Add the wave to current position with smooth transition
      const targetY = currentY + wave * 0.3; // Gentle wave amplitude

      // Smooth interpolation to target (momentum effect)
      currentY += (targetY - currentY) * 0.3;

      // Add small random noise for realism
      currentY += (Math.random() - 0.5) * 0.2;

      // Keep within bounds (2 to 10)
      currentY = Math.max(2, Math.min(10, currentY));

      points.push({
        month,
        rate: currentY,
      });
    });

    return points;
  }, [months]);

  // Animation loop
  useEffect(() => {
    if (chartData.length === 0) return undefined; // Wait for initial data

    const createNewSparkle = () => {
      currentSparkleRef.current = {
        id: sparkleIdRef.current,
        path: generateSparklePath(),
        drawProgress: 0,
        fadeProgress: 0,
      };
      sparkleIdRef.current += 1;
    };

    // Create first sparkle
    createNewSparkle();

    const animate = () => {
      if (!currentSparkleRef.current) return;

      const sparkle = currentSparkleRef.current;

      // Update progress
      if (sparkle.drawProgress < 100) {
        sparkle.drawProgress += 0.8; // Slightly slower for smoother appearance
      }

      // Start fading earlier and slower for smoother snake-like effect
      if (sparkle.drawProgress > 20) {
        sparkle.fadeProgress += 0.3; // Slower fade for longer visible tail
      }

      // If fully drawn and mostly faded, create a new sparkle
      if (sparkle.drawProgress >= 100 && sparkle.fadeProgress >= 85) {
        createNewSparkle();
      }

      // Update every frame for smoothest animation (no throttling)
      setChartData((prevData) => {
        // Create new array with same objects (shallow copy for performance)
        const newData = [...prevData];

        // Reset all sparkle values to null
        for (let i = 0; i < newData.length; i += 1) {
          newData[i] = { ...newData[i], sparkle: null };
        }

        // Update sparkle values
        sparkle.path.forEach((point, pointIndex) => {
          if (newData[pointIndex]) {
            const pointPosition = pointIndex / (sparkle.path.length - 1);
            const drawThreshold = sparkle.drawProgress / 100;
            const fadeThreshold = sparkle.fadeProgress / 100;

            if (pointPosition <= drawThreshold) {
              let opacity = 0.8;

              // Smoother, longer fade region (60% of chart width) for snake-like effect
              if (pointPosition < fadeThreshold) {
                opacity = 0;
              } else if (pointPosition < fadeThreshold + 0.6) {
                // Gradual fade over 60% of the chart width
                const fadeZone = (pointPosition - fadeThreshold) / 0.6;
                // Use cubic easing for even smoother transition
                const easedFade = fadeZone * fadeZone * fadeZone; // Cubic easing
                opacity = easedFade * 0.8;
              }

              if (opacity > 0.01) {
                newData[pointIndex] = { ...newData[pointIndex], sparkle: point.rate };
              }
            }
          }
        });

        return newData;
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [chartData.length, generateSparklePath]);

  return (
    <ResponsiveContainer width='100%' height='100%'>
      <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
        <defs>
          <linearGradient id='sparkleGradient' x1='0%' y1='0%' x2='100%' y2='0%'>
            <stop offset='0%' stopColor='hsl(var(--primary))' stopOpacity='0' />
            <stop offset='20%' stopColor='hsl(var(--primary))' stopOpacity='0' />
            <stop offset='40%' stopColor='hsl(var(--primary))' stopOpacity='0.5' />
            <stop offset='100%' stopColor='hsl(var(--primary))' stopOpacity='0.65' />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray='3 3' stroke='hsl(var(--border))' opacity={0.3} />
        <XAxis dataKey='month' axisLine={false} tickLine={false} tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} />
        <YAxis
          domain={[0, 12]}
          ticks={[0, 2, 4, 6, 8, 10, 12]}
          axisLine={false}
          tickLine={false}
          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
          width={60}
        />
        <Line
          type='monotoneX'
          dataKey='sparkle'
          stroke='url(#sparkleGradient)'
          strokeWidth={2.5}
          dot={false}
          isAnimationActive={false}
          connectNulls
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

export default SparkleChartLoading;
